import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { CARE_HOME_TYPES } from "@/components/common";
import { CheckCircle2, Download } from "lucide-react";

export function LeadMagnetDialog({ open, onOpenChange }) {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", care_home_name: "", role: "" });
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (t) => setTypes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, care_home_types: types, source: "landing" });
      setDone(true);
      toast.success("Checklist sent! Check your inbox.");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lead-magnet-dialog" className="max-w-lg">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-brand text-2xl font-heading">The Oral Health Gap Checklist for Care Homes</DialogTitle>
              <DialogDescription>The 10-minute spot check: 12 boxes every care home should be able to tick before a CQC inspection. Find the gaps — fast.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="lead-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Work email</Label><Input data-testid="lead-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Care home name</Label><Input data-testid="lead-home" required value={form.care_home_name} onChange={(e) => setForm({ ...form, care_home_name: e.target.value })} /></div>
                <div><Label>Your role</Label><Input data-testid="lead-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div>
              </div>
              <div>
                <Label>Care home type <span className="text-muted-foreground font-normal">(select all that apply)</span></Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-auto pr-1">
                  {CARE_HOME_TYPES.map((t) => (
                    <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox data-testid={`lead-type-${t}`} checked={types.includes(t)} onCheckedChange={() => toggle(t)} />{t}
                    </label>
                  ))}
                </div>
              </div>
              <Button data-testid="lead-submit" type="submit" disabled={loading} className="w-full bg-coral hover:bg-coral-hover text-white rounded-full h-11">
                <Download className="h-4 w-4 mr-2" />{loading ? "Sending…" : "Send me the checklist"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-6" data-testid="lead-success">
            <CheckCircle2 className="h-14 w-14 text-rag-green mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-brand font-heading">Your Gap Checklist is on its way</h3>
            <p className="text-muted-foreground mt-2">Check your inbox — or <a data-testid="lead-download" href={`${process.env.REACT_APP_BACKEND_URL}/api/gap-checklist.pdf`} target="_blank" rel="noreferrer" className="text-brand underline font-medium">download it now</a>.</p>
            <div data-testid="lead-bridge" className="mt-5 mb-6 rounded-xl bg-brand text-white text-sm p-4 text-left leading-relaxed">
              Found gaps in your checklist? The platform turns this checklist into a live, tracked audit — with completed evidence at the click of a button. Start your 14-day free trial and see your gaps close in real time.
            </div>
            <Button data-testid="lead-to-trial" onClick={() => { onOpenChange(false); nav("/signup"); }} className="bg-coral hover:bg-coral-hover text-white rounded-full h-11 px-8">Start my 14-day free trial</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
